import { createElement, type ReactNode } from "react";
import { Euro } from "lucide-react";
import type { RevenueDistributionResponse } from "@/types/analytics-revenue";
import { at } from "../../utils/analyticsI18n";
import { formatAnalyticsCurrency } from "../shared/analyticsFormatting";
import type { MetricCardTrend } from "../shared/MetricCard";
import { METRIC_CARD_ICON_COLOR } from "../shared/constants";

const DELTA_SUFFIX = () =>
  at("common.fromPreviousPeriod", " from previous period");

export type ServicesRapidRevenueMetric = {
  title: string;
  value: string;
  delta?: string;
  trend?: MetricCardTrend;
  icon: ReactNode;
  iconColor: string;
};

function toMajorUnits(amount: number, divisor: number): number {
  if (!divisor) {
    return amount;
  }
  return amount / divisor;
}

export function getPartnersRevenueAmount(
  response: RevenueDistributionResponse | undefined,
): number {
  if (!response) {
    return 0;
  }

  const divisor = response.amountDivisor || 1;
  const partnersMinor = response.totals.bySource
    .filter((item) => item.source === "GUESTERIA_PARTNERS")
    .reduce((sum, item) => sum + item.amount, 0);

  if (partnersMinor > 0) {
    return toMajorUnits(partnersMinor, divisor);
  }

  const grandMinor = response.totals.grand.reduce(
    (sum, row) => sum + row.amount,
    0,
  );

  return toMajorUnits(grandMinor, divisor);
}

function formatRevenueValue(amount: number): string {
  return formatAnalyticsCurrency(amount, {
    maximumFractionDigits: 2,
    symbol: "€",
    symbolPosition: "suffix",
  });
}

function percentChange(current: number, previous: number): number | null {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return ((current - previous) / previous) * 100;
}

function formatPercentDelta(change: number | null): string | undefined {
  if (change === null) {
    return undefined;
  }

  const sign = change > 0 ? "+" : "";
  const formatted = change.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

  return `${sign}${formatted}%${DELTA_SUFFIX()}`;
}

function trendFromChange(change: number | null): MetricCardTrend {
  if (change === null || change === 0) {
    return "neutral";
  }

  return change > 0 ? "positive" : "negative";
}

export function buildRapidTotalRevenueMetric(input: {
  current?: RevenueDistributionResponse;
  previous?: RevenueDistributionResponse;
  includeDelta: boolean;
}): ServicesRapidRevenueMetric {
  const currentAmount = getPartnersRevenueAmount(input.current);
  const previousAmount = getPartnersRevenueAmount(input.previous);
  const change = input.includeDelta
    ? percentChange(currentAmount, previousAmount)
    : null;

  return {
    title: at("revenue.totalRevenue", "Total Revenue"),
    value: formatRevenueValue(currentAmount),
    delta: formatPercentDelta(change),
    trend: trendFromChange(change),
    icon: createElement(Euro),
    iconColor: METRIC_CARD_ICON_COLOR,
  };
}

export const RAPID_TOTAL_REVENUE_LOADING_METRIC: ServicesRapidRevenueMetric = {
  title: at("revenue.totalRevenue", "Total Revenue"),
  value: "0€",
  icon: createElement(Euro),
  iconColor: METRIC_CARD_ICON_COLOR,
};
